import { useEffect, useRef, useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { ApplicationRoute } from 'routes/routes.enums';

export const useTimeoutRedirect = (
  timeoutMs: number,
  redirectPath: ApplicationRoute | null,
  sessionStartTimestamp: number | null,
  activityEvents: string[] = [],
) => {
  const navigate = useNavigate();
  const timerRef = useRef<any>(null);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(true);

  const getRemainingTime = useCallback(() => {
    if (!sessionStartTimestamp) {
      return timeoutMs;
    }

    const remainingTime = Date.now() - sessionStartTimestamp;

    return Math.max(0, timeoutMs - remainingTime);
  }, [timeoutMs, sessionStartTimestamp]);

  const resetTimer = useCallback(
    (newTime?: number) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const remainingTime = newTime || getRemainingTime();

      timerRef.current = setTimeout(() => {
        if (redirectPath) {
          navigate(redirectPath);
        }

        setIsTimerActive(false);
      }, remainingTime);
    },
    [navigate, timeoutMs, redirectPath, getRemainingTime],
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!!sessionStartTimestamp) {
      const remainingTime = getRemainingTime();

      if (remainingTime <= 0) {
        if (redirectPath) {
          navigate(redirectPath);
        }

        setIsTimerActive(false);

        return;
      }

      resetTimer();

      return clearTimer;
    }
  }, [sessionStartTimestamp]);

  useEffect(() => {
    if (!activityEvents.length || !sessionStartTimestamp) {
      return;
    }

    const handleActivity = () => {
      resetTimer(timeoutMs);
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  return {
    isTimerActive: isTimerActive,
  };
};
